import {
  collectToggleTargets,
  isCollapsible,
  type ToggleTarget,
} from "@/components/tiptap/collapse/utils";
import { displayHotkey } from "@/utils/hotkeys";

import type { CollapseBlockSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";

function getCollapseTargets(state: EditorState): ToggleTarget[] {
  const { selection, doc } = state;

  if (!selection.empty) {
    return collectToggleTargets(selection, doc);
  }

  const { $from } = selection;
  if ($from.depth === 0) {
    return [];
  }

  const pos = $from.before(1);
  const node = doc.nodeAt(pos);
  return node && isCollapsible(node) ? [{ pos, node }] : [];
}

function lineNumberAt(doc: PMNode, pos: number): number {
  return doc.resolve(pos).index(0);
}

export const collapseSuggestionsProvider: SuggestionProvider<"collapseBlock"> = {
  type: "collapseBlock",
  // Collapsing only flips the `collapsed` attribute, which the lock guard permits.
  allowedWhenLocked: true,
  search: (_str, doc) => {
    const { tiptap } = doc.editor;
    if (!tiptap || (doc.cursor.isActive && !doc.selection.hasSelectedNodes)) {
      return [];
    }

    const { selection } = tiptap.state;
    const { pointerAnchor } = doc.editor.suggestions;

    if (pointerAnchor === null && selection.empty) {
      return [];
    }

    const targets = getCollapseTargets(tiptap.state);
    if (targets.length === 0) {
      return [];
    }

    const positions = targets.map((target) => target.pos);
    const lines = positions.map((pos) => lineNumberAt(tiptap.state.doc, pos));

    const suggestion: CollapseBlockSuggestion = {
      type: "collapseBlock",
      positions,
      collapsed: !!targets[0].node.attrs.collapsed,
      lineRange: [Math.min(...lines), Math.max(...lines)],
      trackingId: targets[0].node.attrs.collapsed ? "expand" : "collapse",
      popupGroup: "collapseBlock",
      priority: 1,
      icon: "Collapse",
      hotkey: displayHotkey("collapse-block"),
    };

    return [[suggestion]];
  },
};
